import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, Pencil, Flag, Target, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import GanttChart from "../../components/GanttChart";
import { fmtDateGB } from "../../lib/format";
import TaskModal from "../../components/TaskModal";

export default function TasksTab({ projectId, tasks, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { mode, kind, existing? }
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all"); // all | task | milestone | goal

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const seed = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/seed-defaults/${projectId}`);
      toast.success("Default UK installation timeline added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const updTask = async (tid, payload) => {
    try {
      await api.patch(`/tasks/${tid}`, payload);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delTask = async (tid) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/tasks/${tid}`);
    onSaved();
  };

  const counts = useMemo(() => {
    const c = { all: tasks.length, task: 0, milestone: 0, goal: 0 };
    for (const t of tasks) {
      if (t.goal) c.goal += 1;
      else if (t.milestone) c.milestone += 1;
      else c.task += 1;
    }
    return c;
  }, [tasks]);

  const filtered = useMemo(() => {
    if (filter === "all") return tasks;
    if (filter === "milestone") return tasks.filter((t) => t.milestone && !t.goal);
    if (filter === "goal") return tasks.filter((t) => t.goal);
    return tasks.filter((t) => !t.milestone && !t.goal);
  }, [tasks, filter]);

  const ganttTasks = useMemo(() => tasks.filter((t) => !t.goal), [tasks]);
  const goals = useMemo(() => tasks.filter((t) => t.goal), [tasks]);

  return (
    <div className="space-y-6" data-testid="tasks-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label">Execution · Project management</div>
          <div className="font-display text-2xl font-semibold mt-1">Gantt timeline</div>
          <div className="muted text-xs mt-1">
            Click bars to update progress. Use the buttons below to add tasks, milestones or project goals.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setModal({ mode: "create", kind: "task" })}
            className="btn-accent"
            data-testid="add-task-btn"
          >
            <Plus size={13} /> Add task
          </button>
          <button
            onClick={() => setModal({ mode: "create", kind: "milestone" })}
            className="btn-ghost"
            data-testid="add-milestone-btn"
          >
            <Flag size={13} /> Add milestone
          </button>
          <button
            onClick={() => setModal({ mode: "create", kind: "goal" })}
            className="btn-ghost"
            data-testid="add-goal-btn"
          >
            <Target size={13} /> Add goal
          </button>
          {tasks.length === 0 && (
            <button onClick={seed} disabled={busy} className="btn-ghost" data-testid="seed-tasks-btn"
                    title="Applies the timeline configured in Settings → Default project timeline">
              <Plus size={13} /> {busy ? "Adding…" : "Seed default UK timeline"}
            </button>
          )}
        </div>
      </div>

      <GanttChart
        tasks={ganttTasks}
        onTaskClick={(t) => {
          // Open edit modal on bar click
          const full = tasks.find((x) => x.id === t.id);
          if (full) setModal({ mode: "edit", existing: full });
        }}
      />

      {/* Project Goals card — always rendered so tests can assert presence */}
      <div className="swiss-card-flat p-5" data-testid="goals-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target size={14} className="text-[var(--accent-hover)]" />
            <div className="label">Project goals</div>
          </div>
          <div className="muted text-xs">{goals.length} total</div>
        </div>
        {goals.length === 0 ? (
          <div className="muted text-xs text-center py-4" data-testid="goals-empty">
            No goals yet. Use the &ldquo;Add goal&rdquo; button above to capture high-level project objectives.
          </div>
        ) : (
          <ul className="space-y-2">
            {goals.map((g) => (
              <li
                key={g.id}
                className="flex items-center justify-between border-b border-[var(--line-soft)] pb-2 last:border-0"
                data-testid={`goal-row-${g.id}`}
              >
                <div className="flex items-center gap-3">
                  <Target size={12} className="text-[var(--accent-hover)]" />
                  <div>
                    <div className="font-medium text-sm">{g.name}</div>
                    {g.description && <div className="muted text-xs">{g.description}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs muted">{fmtDateGB(g.start_date)}</span>
                  <span className={`tag ${g.status === "done" ? "ok" : "signal"}`}>{g.status}</span>
                  <button onClick={() => setModal({ mode: "edit", existing: g })} data-testid={`goal-edit-${g.id}`}>
                    <Pencil size={13} className="muted" />
                  </button>
                  <button onClick={() => delTask(g.id)} data-testid={`goal-del-${g.id}`}>
                    <Trash2 size={13} className="muted" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="task-type-filters">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="task-filter-all">
          All <span className="ml-1 font-mono opacity-60">{counts.all}</span>
        </FilterChip>
        <FilterChip active={filter === "task"} onClick={() => setFilter("task")} testid="task-filter-task">
          <ListChecks size={11} className="inline mr-1" /> Tasks <span className="ml-1 font-mono opacity-60">{counts.task}</span>
        </FilterChip>
        <FilterChip active={filter === "milestone"} onClick={() => setFilter("milestone")} testid="task-filter-milestone">
          <Flag size={11} className="inline mr-1" /> Milestones <span className="ml-1 font-mono opacity-60">{counts.milestone}</span>
        </FilterChip>
        <FilterChip active={filter === "goal"} onClick={() => setFilter("goal")} testid="task-filter-goal">
          <Target size={11} className="inline mr-1" /> Goals <span className="ml-1 font-mono opacity-60">{counts.goal}</span>
        </FilterChip>
      </div>

      <div className="swiss-card-flat overflow-hidden">
        <table className="data-table" data-testid="tasks-table">
          <thead>
            <tr>
              <th>Item</th><th>Start</th><th>End</th><th>Status</th><th>Progress</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} data-testid={`task-row-${t.id}`}>
                <td className="font-medium">
                  {t.goal ? <Target size={11} className="inline mr-1 text-[var(--accent-hover)]" /> :
                    t.milestone ? <Flag size={11} className="inline mr-1 text-[var(--accent-hover)]" /> :
                      <ListChecks size={11} className="inline mr-1 muted" />}
                  {t.name}
                </td>
                <td className="font-mono text-xs">{fmtDateGB(t.start_date)}</td>
                <td className="font-mono text-xs">{t.goal ? "—" : fmtDateGB(t.end_date)}</td>
                <td>
                  <select
                    data-testid={`task-status-${t.id}`}
                    value={t.status}
                    onChange={(e) =>
                      updTask(t.id, {
                        status: e.target.value,
                        progress_pct: e.target.value === "done" ? 100 : t.progress_pct,
                      })
                    }
                    className="input-box text-xs"
                  >
                    <option value="not_started">Not started</option>
                    <option value="in_progress">In progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="done">Done</option>
                  </select>
                </td>
                <td className="num font-mono">{t.goal ? "—" : `${t.progress_pct}%`}</td>
                <td className="num">
                  <button onClick={() => setModal({ mode: "edit", existing: t })} className="mr-2" data-testid={`task-edit-${t.id}`}>
                    <Pencil size={13} className="muted" />
                  </button>
                  <button onClick={() => delTask(t.id)} data-testid={`task-del-${t.id}`}>
                    <Trash2 size={13} className="muted" />
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={6} className="muted text-center py-8" data-testid="tasks-empty">
                  No items in this view. Add your first task, milestone or goal above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <TaskModal
          projectId={projectId}
          existing={modal.existing}
          defaultKind={modal.kind || "task"}
          tasks={tasks}
          users={users}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            onSaved();
          }}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active
          ? "bg-[var(--accent)] text-white border-[var(--accent)]"
          : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--accent)]"
      }`}
    >
      {children}
    </button>
  );
}
